import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CirclePause from '@gravity-ui/icons/CirclePause';
import { Button, Icon, Label, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { republishAliceAgentApp, unpublishAliceAgentApp } from 'client/api/services/aliceAgentApps';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import * as keyset from 'i18n/AliceAgentAppPublishedActions';

import styles from './AliceAgentAppEditPage.module.scss';

const t = i18n(keyset);

interface Props {
    app: AliceAgentAppDTO;
    draftUrl: string;
    onSaved: () => Promise<unknown> | void;
}

const getErrorMessage = (err: unknown, fallback: string) =>
    err && typeof err === 'object' && 'message' in err && typeof err.message === 'string' ? err.message : fallback;

export const AliceAgentAppPublishedActions: FC<Props> = ({ app, draftUrl, onSaved }) => {
    const navigate = useNavigate();
    const { add: toasterAdd } = useToaster();
    const [confirmUnpublishOpen, setConfirmUnpublishOpen] = useState(false);
    const [isUnpublishing, setIsUnpublishing] = useState(false);
    const [isPublishingVersion, setIsPublishingVersion] = useState(false);

    if (!app.published) return null;

    const refreshAfterSuccessfulAction = async () => {
        try {
            await onSaved();
        } catch {
            toasterAdd({
                title: t('toast-refresh-error'),
                theme: 'danger',
                name: 'REFRESH_ALICE_AGENT_APP',
            });
        }
    };

    const handleUnpublish = async () => {
        if (isUnpublishing) return;
        setIsUnpublishing(true);
        try {
            await unpublishAliceAgentApp(app.skillId);
            toasterAdd({ title: t('toast-unpublished'), theme: 'success', name: 'UNPUBLISH_ALICE_AGENT_APP' });
            await refreshAfterSuccessfulAction();
            setConfirmUnpublishOpen(false);
            navigate(draftUrl);
        } catch (err) {
            toasterAdd({
                title: getErrorMessage(err, t('toast-unpublish-error')),
                theme: 'danger',
                name: 'UNPUBLISH_ALICE_AGENT_APP',
            });
        } finally {
            setIsUnpublishing(false);
        }
    };

    const handlePublishVersion = async () => {
        if (isPublishingVersion) return;
        setIsPublishingVersion(true);
        try {
            await republishAliceAgentApp(app.skillId);
            toasterAdd({
                title: t('toast-published-version'),
                theme: 'success',
                name: 'PUBLISH_PUBLISHED_ALICE_AGENT_APP',
            });
            await refreshAfterSuccessfulAction();
        } catch (err) {
            toasterAdd({
                title: getErrorMessage(err, t('toast-publish-version-error')),
                theme: 'danger',
                name: 'PUBLISH_PUBLISHED_ALICE_AGENT_APP',
            });
        } finally {
            setIsPublishingVersion(false);
        }
    };

    return (
        <div className={styles.footer}>
            {app.onAir ? (
                <>
                    <Button size="l" onClick={() => setConfirmUnpublishOpen(true)} disabled={isUnpublishing}>
                        <Icon data={CirclePause} />
                        {t('action-unpublish')}
                    </Button>
                    {confirmUnpublishOpen && (
                        <ModalWithConfirmation
                            title={t('unpublish-confirm-title')}
                            text={t('unpublish-confirm-text')}
                            confirmButtonLabel={t('action-unpublish')}
                            onClose={() => setConfirmUnpublishOpen(false)}
                            onConfirm={handleUnpublish}
                            loading={isUnpublishing}
                        />
                    )}
                </>
            ) : (
                <>
                    <Label theme="warning" icon={<Icon data={CirclePause} />} size="m">
                        {t('status-unpublished')}
                    </Label>
                    <Button
                        view="action"
                        size="l"
                        onClick={handlePublishVersion}
                        loading={isPublishingVersion}
                        disabled={isPublishingVersion}
                    >
                        {t('action-publish-version')}
                    </Button>
                </>
            )}
        </div>
    );
};
